import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Users, Video as VideoIcon, Eye, ArrowLeft, Info } from "lucide-react";
import { API } from "@/lib/config";
import { usePlayer } from "@/components/contexts/PlayerContext";
import { CompactGrid } from "@/components/VideoCardCompact";

/**
 * A channel's profile and recent uploads.
 *
 * Channel names were already rendered on every card but were not links; this gives
 * that text somewhere to go, and reuses the avatar and subscriber data already
 * being fetched for the cards.
 */
export const ChannelPage = () => {
  const { channelId } = useParams();
  const { play } = usePlayer();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/channel/${channelId}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          // 503 is the backend saying it has neither budget nor an indexed copy
          // of this channel - the same situation as 429, told a different way.
          const status = e.response?.status;
          setError(status === 429 || status === 503 ? "quota" : "failed");
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [channelId]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen pt-24 px-4 text-center">
        <p className="text-sm text-white/50">
          {error === "quota"
            ? "YouTube's daily search allowance is spent — channel pages return once it resets."
            : "Could not load this channel."}
        </p>
        <Link to="/" className="inline-block mt-4 text-sm text-orange-400 hover:underline">
          Back to trending
        </Link>
      </div>
    );
  }

  const { channel, videos, notice } = data;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="channel-page">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-orange-400 mb-6 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>

        <div className="flex items-center gap-4 mb-8">
          {channel.avatar && (
            <img
              src={channel.avatar}
              alt={channel.title}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border border-white/10"
            />
          )}
          <div className="min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-black truncate">{channel.title}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-white/50">
              {channel.subscriber_count && (
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3 h-3" /> {channel.subscriber_count} subscribers
                </span>
              )}
              {channel.video_count && (
                <span className="inline-flex items-center gap-1">
                  <VideoIcon className="w-3 h-3" /> {channel.video_count} videos
                </span>
              )}
              {channel.view_count && (
                <span className="inline-flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {channel.view_count} views
                </span>
              )}
            </div>
          </div>
        </div>

        {channel.description && (
          <p className="text-xs text-white/50 mb-8 max-w-3xl whitespace-pre-line line-clamp-4">
            {channel.description}
          </p>
        )}

        {notice && (
          <div
            className="mb-6 flex items-start gap-2 rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-xs text-orange-200"
            data-testid="channel-notice"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{notice}</span>
          </div>
        )}

        <h2 className="font-heading text-lg font-bold mb-4">Latest uploads</h2>
        <CompactGrid videos={videos} onPlay={play} emptyMessage="No uploads found." />
      </div>
    </div>
  );
};
